import React, { useRef, useState } from 'react';
import {
  useListCoversQuery,
  useUploadCoverMutation,
  useDeleteCoverMutation,
} from '../../redux/covers/covers.api';

export const AdminCovers = (): React.JSX.Element => {
  const { data, isLoading, error } = useListCoversQuery();
  const [uploadCover, { isLoading: isUploading }] = useUploadCoverMutation();
  const [deleteCover] = useDeleteCoverMutation();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const hasItems = data && data.length > 0;
  const empty = data && data.length === 0;

  const handleFiles = async (files: FileList | null): Promise<void> => {
    if (!files || files.length === 0) return;
    setUploadError(null);
    try {
      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append('file', file);
        await uploadCover(formData).unwrap();
      }
    } catch {
      setUploadError('Failed to upload one or more covers. Try again.');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (id: string): Promise<void> => {
    if (!window.confirm('Delete this cover? This cannot be undone.')) return;
    setDeletingId(id);
    try {
      await deleteCover(id).unwrap();
    } catch {
      // eslint-disable-next-line no-alert
      window.alert('Failed to delete cover. Try again.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="admin-users">
      <div className="admin-users__header">
        <div className="admin-users__title">
          <h2>Covers</h2>
          {data && (
            <span className="admin-users__count">
              {data.length.toLocaleString()} total
            </span>
          )}
        </div>
        <button
          type="button"
          className="admin-users__page-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
        >
          {isUploading ? 'Uploading…' : '+ Add cover'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => void handleFiles(e.target.files)}
        />
      </div>

      {(error || uploadError) && (
        <div className="admin-users__error">
          {uploadError ?? 'Failed to load covers. Try again.'}
        </div>
      )}

      {isLoading && !data && (
        <div className="admin-users__loading">Loading covers…</div>
      )}

      {empty && (
        <div className="admin-tab-empty">
          <h2 className="admin-tab-empty__title">No covers</h2>
          <p className="admin-tab-empty__sub">
            Add a cover to get started.
          </p>
        </div>
      )}

      {hasItems && (
        <div className="admin-covers__grid">
          {data!.map((cover) => (
            <div key={cover.id} className="admin-cover-card">
              <img
                className="admin-cover-card__img"
                src={cover.imageUrl}
                alt={cover.filename ?? 'Cover'}
                loading="lazy"
              />
              <button
                type="button"
                className="admin-cover-card__delete"
                onClick={() => void handleDelete(cover.id)}
                disabled={deletingId === cover.id}
                aria-label="Delete cover"
              >
                {deletingId === cover.id ? '…' : '×'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
